import { CHECKLIST_TEMPLATES, CONDITIONAL_TEMPLATE_ROWS } from '../../checklistTemplates';

// NY AUTHORITY VERSION RECORD — DATA (docs/KNOWLEDGE-IMPORT-MANIFEST.md K-23).
//
// Source: docs/authority/ny/NY-HOME-INSPECTION-SCOPE-AUTHORITY.md §8 (the frozen version block) and
// §16 / docs/authority/ny/NY-OWNER-RULINGS-RATIFICATION-20260909.md §4 / §5 (the 2026-09-09 Owner
// rulings that closed the two release blockers). Frozen plan §5.5 places this module in NY F1
// Round 4 as the single compiled-in record of WHICH authority the product implements.
//
// It is DATA, exactly like src/domain/agreementText.ts: versioned, testable, never fetched, never
// edited at runtime. Nothing here decides anything; the snapshot (src/domain/scopeGate.ts) records
// which version produced an inspection's dispositions, and the gate carries it on every decision so
// the code can always answer "which authority version decided this?" (runtime contract §1).
export const NY_AUTHORITY_VERSION = 'NY-SCOPE-AUTHORITY-2026-09-06-F1';

// The agreement / checklist / AI protocol versions are recorded per inspection in the scope
// snapshot; the two constants below are the compiled-in current values of the authority record.
export type NyAuthorityStatus = 'candidate' | 'correctedCandidate' | 'ratified' | 'superseded';

export type NyReleaseBlockerId = 'NEW_CONSTRUCTION' | 'AI_CLOUD_DISCLOSURE';

// C2 wrote two counsel states (LEGAL_COUNSEL_REQUIRED / RESOLVED). The Owner added a third on
// 2026-09-09 by closing both blockers on the record with explicit risk acceptance; the counsel
// question itself stays open as an ADVISORY entry in the legal-review queue.
export type NyReleaseBlockerStatus = 'LEGAL_COUNSEL_REQUIRED' | 'OWNER_CLOSED_RISK_ACCEPTED' | 'RESOLVED';

export interface NyReleaseBlocker {
  id: NyReleaseBlockerId;
  status: NyReleaseBlockerStatus;
  lrIds: readonly string[];
  // The Owner ruling that closed it, and what still has to be implemented before the capability
  // may be used. Neither line authorises a release (master §16; ratification §5).
  ownerRuling: string;
  implementationGate: string;
  // The conservative product block that stays in force until the implementation gate is met.
  productBlockInForce: boolean;
}

export const NY_RELEASE_BLOCKERS: readonly NyReleaseBlocker[] = Object.freeze([
  Object.freeze({
    id: 'NEW_CONSTRUCTION' as const,
    status: 'OWNER_CLOSED_RISK_ACCEPTED' as const,
    lrIds: Object.freeze(['LR-04', 'LR-21']),
    ownerRuling: 'OD-9 (2026-09-09): New Build variant flow replaces the PP-20 block',
    implementationGate: 'Round 5 — New Build inspection type, agreement and report variant',
    productBlockInForce: true
  }),
  Object.freeze({
    id: 'AI_CLOUD_DISCLOSURE' as const,
    status: 'OWNER_CLOSED_RISK_ACCEPTED' as const,
    lrIds: Object.freeze(['LR-02', 'LR-19', 'LR-20']),
    ownerRuling: 'OD-8 (2026-09-09): agreement v2 consent clause + agreement-version gate',
    implementationGate: 'Round 5 — agreement v2 + version gate; Round 4 Scope Gate is the technical prerequisite',
    productBlockInForce: true
  })
]);

// FNV-1a, 32 bit — a stable fingerprint, not a security hash. It exists so the checklist authority
// version changes the moment a template row id or title changes, instead of drifting silently away
// from the snapshot that claims to pin it.
function fingerprint(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

const CHECKLIST_ROWS = Object.values(CHECKLIST_TEMPLATES).flat();

// checklistTemplates@af31c23 was 126 starter rows; Round 2 added the three CONDITIONAL rows and
// corrected two row texts (CK-05 / CK-24). The runtime value is derived from the rows themselves.
export const NY_CHECKLIST_AUTHORITY_VERSION = `checklistTemplates-${CHECKLIST_ROWS.length}+${CONDITIONAL_TEMPLATE_ROWS.length}-${fingerprint(
  [...CHECKLIST_ROWS, ...CONDITIONAL_TEMPLATE_ROWS].map((row) => `${row.id}|${row.title}`).join('\n')
)}`;

export interface NyAuthorityRecord {
  jurisdiction: 'NY';
  authorityVersion: string;
  status: NyAuthorityStatus;
  verifiedAt: string;
  ratifiedAt: string;
  sourceRegistryVersion: string;
  legalTextSnapshot: string;
  checklistAuthorityVersion: string;
  supersedes: string | null;
  supersededBy: string | null;
  releaseBlockers: readonly NyReleaseBlocker[];
  // What F1 does NOT mean (master §8): no commercial release, no legal clearance, no compliance
  // declaration. Recorded so no caller can read "ratified" as "approved to ship".
  nyCommercialRelease: 'NOT AUTHORISED';
}

export const NY_AUTHORITY_RECORD: NyAuthorityRecord = Object.freeze({
  jurisdiction: 'NY',
  authorityVersion: NY_AUTHORITY_VERSION,
  status: 'ratified',
  verifiedAt: '2026-09-05',
  ratifiedAt: '2026-09-06',
  sourceRegistryVersion: 'NY-SOURCE-REGISTRY-2026-09-05-R1',
  legalTextSnapshot: 'NY-LEGAL-TEXT-VERBATIM-20260905',
  checklistAuthorityVersion: NY_CHECKLIST_AUTHORITY_VERSION,
  supersedes: 'NY-SCOPE-AUTHORITY-2026-09-05-C2',
  supersededBy: null,
  releaseBlockers: NY_RELEASE_BLOCKERS,
  nyCommercialRelease: 'NOT AUTHORISED'
});
